import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import {
  Button, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { useApi } from 'lib/contexts/ApplicationState';
import { useState, useEffect } from 'react';
import { Post, PostMetricsUpdate, PostMetrics } from 'lib/types/posts';
import { convertZuluDate } from 'lib/utils/datetime';

interface Props {
  isOpen: boolean;
  updateMetrics: (arg: PostMetricsUpdate) => void;
  onRequestClose: () => void;
  post: Post;
}

const initalMetrics = {
  currentLikes: 0,
  currentComments: 0,
  currentViews: 0,
  currentShares: 0,
  currentPlays: 0,
  currentImpressions: 0,
};

export const UpdatePostMetricsModal = ({
  updateMetrics, onRequestClose, isOpen, post,
}: Props) => {
  const API = useApi();
  const [postMetrics, setPostMetrics] = useState(initalMetrics);
  const [postMetricsRows, setPostMetricsRows] = useState<PostMetrics[]>([]);

  useEffect(() => {
    const getPostMetricRows = async () => {
      const metricRows = await API.getAllPostMetricsForPost(post.id);
      setPostMetricsRows(metricRows);
    };

    if (isOpen) {
      getPostMetricRows();
    }
  }, [post, API, isOpen]);

  const headCells = [
    { accessor: 'date', label: 'Date' },
    { accessor: 'likes', label: 'Likes' },
    { accessor: 'views', label: 'Views' },
    { accessor: 'shares', label: 'Shares' },
    { accessor: 'comments', label: 'Comments' },
    { accessor: 'plays', label: 'Plays' },
    { accessor: 'impressions', label: 'Impressions' },
  ];

  const displayPostMetricRows = () => (
    <div className="overflow-x: auto">
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {headCells.map((hc) => (
              <TableCell
                className="border"
                key={hc.accessor}
              >
                {hc.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell className="border" />
            {/* purposefully left empty */}
            <TableCell className="border">
              <input
                className="bg-hintgray dark:bg-darkgray p-2 w-full rounded-lg outline"
                onChange={(e) => setPostMetrics({
                  ...postMetrics, currentLikes: parseInt(e.target.value, 10),
                })}
                value={postMetrics.currentLikes || 0}
              />
            </TableCell>
            <TableCell className="border">
              <input
                className="bg-hintgray dark:bg-darkgray p-2 w-full rounded-lg outline"
                onChange={(e) => setPostMetrics({
                  ...postMetrics, currentViews: parseInt(e.target.value, 10),
                })}
                value={postMetrics.currentViews || 0}
              />
            </TableCell>
            <TableCell className="border">
              <input
                className="bg-hintgray dark:bg-darkgray p-2 w-full rounded-lg outline"
                onChange={(e) => setPostMetrics({
                  ...postMetrics, currentShares: parseInt(e.target.value, 10),
                })}
                value={postMetrics.currentShares || 0}
              />
            </TableCell>
            <TableCell className="border">
              <input
                className="bg-hintgray dark:bg-darkgray p-2 w-full rounded-lg outline"
                onChange={(e) => setPostMetrics({
                  ...postMetrics, currentComments: parseInt(e.target.value, 10),
                })}
                value={postMetrics.currentComments || 0}
              />
            </TableCell>
            <TableCell className="border">
              <input
                className="bg-hintgray dark:bg-darkgray p-2 w-full rounded-lg outline"
                onChange={(e) => setPostMetrics({
                  ...postMetrics, currentPlays: parseInt(e.target.value, 10),
                })}
                value={postMetrics.currentPlays || 0}
              />
            </TableCell>
            <TableCell className="border">
              <input
                className="bg-hintgray dark:bg-darkgray p-2 w-full rounded-lg outline"
                onChange={(e) => setPostMetrics({
                  ...postMetrics, currentImpressions: parseInt(e.target.value, 10),
                })}
                value={postMetrics.currentImpressions || 0}
              />
            </TableCell>
          </TableRow>
          {postMetricsRows.map((row) => (
            <TableRow key={row.id} className="odd:bg-hintgray dark:bg-darkgray dark:odd:bg-[#121212]">
              <TableCell className="border p-4">{convertZuluDate(row.createdAt)}</TableCell>
              <TableCell className="border p-4">{(row.likes || 0).toLocaleString()}</TableCell>
              <TableCell className="border p-4">{(row.views || 0).toLocaleString()}</TableCell>
              <TableCell className="border p-4">{(row.shares || 0).toLocaleString()}</TableCell>
              <TableCell className="border p-4">{(row.comments || 0).toLocaleString()}</TableCell>
              <TableCell className="border p-4">{(row.plays || 0).toLocaleString()}</TableCell>
              <TableCell className="border p-4">{(row.impressions || 0).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const disableUpdateButton = (post.currentLikes === postMetrics.currentLikes
    && post.currentViews === postMetrics.currentViews
    && post.currentComments === postMetrics.currentComments
    && post.currentShares === postMetrics.currentShares
    && post.currentPlays === postMetrics.currentPlays
    && post.currentImpressions === postMetrics.currentImpressions);

  useEffect(() => {
    if (post) {
      setPostMetrics({
        currentLikes: post.currentLikes,
        currentComments: post.currentComments,
        currentViews: post.currentViews,
        currentShares: post.currentShares,
        currentPlays: post.currentPlays,
        currentImpressions: post.currentImpressions,
      });
    }
  }, [post]);

  return (
    <Dialog
      open={isOpen}
      onClose={onRequestClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
    >
      <DialogTitle id="alert-dialog-title">
        Manually update post metrics.
        <div className="absolute top-2 right-2 text-orange">
          <Button
            onClick={() => updateMetrics(postMetrics)}
            color="primary"
            disabled={disableUpdateButton}
          >
            Update
          </Button>
          <Button color="inherit" size="small" onClick={onRequestClose}>
            Cancel
          </Button>
        </div>
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {displayPostMetricRows()}
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};
